import React from 'react';
import { AD } from './admin.constants';
import { s } from './admin.styles';
import { ADIcon } from './ADIcon';
import { ToolRecord } from './admin.types';

function buildToolSchema(tool: ToolRecord): string {
  const requiredParams = tool.params.filter((p) => p.required).map((p) => '"' + p.name + '"').join(', ');
  const propLines = tool.params.map((p) => {
    const typeName = p.type.split(':')[0].trim();
    const descEscaped = p.desc.replace(/"/g, '\\"');
    return '      "' + p.name + '": { "type": "' + typeName + '", "description": "' + descEscaped + '" }';
  }).join(',\n');
  const returnsEscaped = tool.returns.replace(/"/g, '\\"');
  return '{\n  "name": "' + tool.name + '",\n  "description": "' + tool.description + '",\n  "parameters": {\n    "type": "object",\n    "required": [' + requiredParams + '],\n    "properties": {\n' + propLines + '\n    }\n  },\n  "returns": "' + returnsEscaped + '"\n}';
}

function EnabledToggle({ tool, onToggle }: { tool: ToolRecord; onToggle: (id: string, enabled: boolean) => void }) {
  const isEnabled = tool.status === 'published';
  const dotColor = isEnabled ? AD.GREEN : AD.AMBER;
  const label = isEnabled ? 'Enabled' : 'Disabled';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: AD.INK_700, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={e => onToggle(tool.id, e.target.checked)}
          aria-label="Toggle enabled"
        />
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
        {label}
      </label>
    </div>
  );
}

interface ToolEditorProps {
  tool: ToolRecord;
  onEdit: (id: string, value: string) => void;
  onToggleEnabled?: (id: string, enabled: boolean) => void;
}

export function ToolEditor({ tool, onEdit, onToggleEnabled }: ToolEditorProps) {
  const paramCount = tool.params.length;
  const linkedPromptCount = tool.linkedPrompts.length;
  const linkedPromptLabel = 'Used by · ' + linkedPromptCount + ' prompt' + (linkedPromptCount === 1 ? '' : 's');
  const paramsLabel = 'Parameters · ' + paramCount;
  const schemaValue = buildToolSchema(tool);
  return (
    <>
      <div style={s.descBox}>
        <div style={s.descLab}>Description (the LLM reads this)</div>
        <textarea
          defaultValue={tool.description}
          onChange={e => onEdit(tool.id, e.target.value)}
          style={s.descInput}
          aria-label="Tool description"
        />
      </div>

      {onToggleEnabled && tool.aiToolId && <EnabledToggle tool={tool} onToggle={onToggleEnabled} />}

      <div style={s.smallCard}>
        <div style={s.smallCardHead}>
          <ADIcon name="bolt" size={14} color={AD.PURPLE_STRONG} />
          <div style={s.smallCardTitle}>{paramsLabel}</div>
          <span style={s.smallCardLink}>+ Add parameter</span>
        </div>
        <div>
          {tool.params.map((p, i) => {
            const rowStyle = i === 0 ? { ...s.paramRow, ...s.paramRowFirst } : s.paramRow;
            return (
              <div key={p.name} style={rowStyle}>
                <span style={s.paramName}>{p.name}</span>
                <span style={s.paramType}>{p.type}</span>
                <span style={s.paramDesc}>
                  {p.required && <span style={s.paramReq}>REQ</span>}
                  {p.desc}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={s.editorCard}>
        <div style={s.editorTopRow}>
          <div style={s.editorTabs}>
            <span style={{ ...s.editorTab, ...s.editorTabActive }}>schema.json</span>
            <span style={s.editorTab}>examples</span>
            <span style={s.editorTab}>impl notes</span>
          </div>
          <div style={s.editorMeta}>
            <span>{'JSON · ' + paramCount + ' props'}</span>
            <span style={{ color: '#34d399' }}>● valid</span>
          </div>
        </div>
        <textarea defaultValue={schemaValue} style={s.editorTextarea} aria-label="Tool schema" />
      </div>

      <div style={s.smallCard}>
        <div style={s.smallCardHead}>
          <ADIcon name="link" size={14} color={AD.PURPLE_STRONG} />
          <div style={s.smallCardTitle}>{linkedPromptLabel}</div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {tool.linkedPrompts.map((promptName) => (
            <span key={promptName} style={{ ...s.chip, background: AD.PURPLE_SOFT, color: AD.PURPLE_STRONG }}>{promptName}</span>
          ))}
        </div>
      </div>
    </>
  );
}
